import { MODELS } from './models.js';

// Approximate char-to-token ratio for English; tighter than 4/1 for prompts that
// include JSON, code, and inline structure (which tokenize denser).
const CHARS_PER_TOKEN = 3.8;

export function estimateInputTokens(text) {
  if (!text) return 0;
  const chars = text.length;
  return Math.max(1, Math.ceil(chars / CHARS_PER_TOKEN));
}

export function estimateOutputTokens(maxTokens, factor = 0.7) {
  // Average completion uses ~70% of max_tokens budget on substantive tasks.
  return Math.max(1, Math.round((maxTokens || 512) * factor));
}

export function costFor(modelKey, inputTokens, outputTokens) {
  const m = MODELS[modelKey];
  if (!m) return 0;
  return (
    (inputTokens / 1_000_000) * m.inputPer1M +
    (outputTokens / 1_000_000) * m.outputPer1M
  );
}

export function costBreakdown(modelKey, inputTokens, outputTokens) {
  const m = MODELS[modelKey];
  if (!m) return { input: 0, output: 0, total: 0 };
  const input = (inputTokens / 1_000_000) * m.inputPer1M;
  const output = (outputTokens / 1_000_000) * m.outputPer1M;
  return { input, output, total: input + output };
}

/**
 * Estimate cost of a full orchestration run.
 * Returns per-agent breakdown, judge breakdown (if synthesis on), and totals.
 */
export function orchestrationEstimate({
  task,
  systemPrompts = [],
  modelKeys,
  maxTokens = 512,
  synthesis = false,
  judgeKey = 'claude-sonnet',
}) {
  const taskTokens = estimateInputTokens(task);
  const out = estimateOutputTokens(maxTokens);

  const perAgent = modelKeys.map((key, i) => {
    const sp = systemPrompts[i] || '';
    const inTok = taskTokens + estimateInputTokens(sp);
    return {
      key,
      label: MODELS[key]?.label || key,
      color: MODELS[key]?.color || '#94a8c4',
      inputTokens: inTok,
      outputTokens: out,
      ...costBreakdown(key, inTok, out),
    };
  });

  const subtotal = perAgent.reduce((s, a) => s + a.total, 0);

  let judge = null;
  if (synthesis) {
    // Judge sees task + every agent's full output
    const judgeIn = taskTokens + perAgent.length * out + 200; // judge meta-prompt
    const judgeOut = Math.round(out * 0.6);
    judge = {
      key: judgeKey,
      label: MODELS[judgeKey]?.label || judgeKey,
      color: MODELS[judgeKey]?.color || '#94a8c4',
      inputTokens: judgeIn,
      outputTokens: judgeOut,
      ...costBreakdown(judgeKey, judgeIn, judgeOut),
    };
  }

  return {
    perAgent,
    judge,
    subtotal,
    total: subtotal + (judge?.total || 0),
    inputTokens: perAgent.reduce((s, a) => s + a.inputTokens, 0) + (judge?.inputTokens || 0),
    outputTokens: perAgent.reduce((s, a) => s + a.outputTokens, 0) + (judge?.outputTokens || 0),
  };
}

// Compare cost of running same task across all available models (single agent each)
export function compareAcrossModels({ task, maxTokens = 512, modelKeys }) {
  const inTok = estimateInputTokens(task);
  const outTok = estimateOutputTokens(maxTokens);
  return modelKeys
    .map((key) => ({
      key,
      label: MODELS[key]?.label || key,
      vendor: MODELS[key]?.vendor || '',
      tier: MODELS[key]?.tier || 0,
      color: MODELS[key]?.color || '#94a8c4',
      ...costBreakdown(key, inTok, outTok),
      inputTokens: inTok,
      outputTokens: outTok,
    }))
    .sort((a, b) => a.total - b.total);
}

// Project monthly spend at N runs/day
export function projectMonthly(perRun, runsPerDay) {
  return perRun * runsPerDay * 30.4375;
}

// Formatters
export function fmtUSD(n) {
  if (n == null || Number.isNaN(n)) return '—';
  if (n === 0) return '$0.00';
  if (n < 0.0001) return `$${(n * 1_000_000).toFixed(2)}μ`; // microdollars
  if (n < 0.01) return `$${n.toFixed(5)}`;
  if (n < 1) return `$${n.toFixed(4)}`;
  if (n < 100) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(2)}`;
}

export function fmtTokens(n) {
  if (n == null) return '—';
  if (n < 1_000) return `${n}`;
  if (n < 1_000_000) return `${(n / 1_000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

export function fmtMs(ms) {
  if (ms == null) return '—';
  if (ms < 1_000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1_000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}
